import { t } from "../../i18n";
import { useEffect, useRef, type KeyboardEvent } from "react";
import { useNavigate } from "react-router-dom";
import { useCadUi } from "../../state/cadUiStore";
import { runCommandLine, suggestCommands } from "../command/commands";
import { SKETCH_PROMPT } from "../command/sketch";

const TONE: Record<string, string> = { echo: "text-cad-cmdecho", out: "text-cad-cmdink", ok: "text-cad-cmdok", err: "text-cad-cmderr" };

/** Komut satırı (README §2.5): 44 px geçmiş + 24 px giriş; Enter çalıştırır, boş Enter son komutu yineler, Esc temizler. */
export function CommandLine({ disabled = false }: { disabled?: boolean }): JSX.Element {
  const nav = useNavigate();
  const value = useCadUi((s) => s.command.value);
  const history = useCadUi((s) => s.command.history);
  const setValue = useCadUi((s) => s.setCommandValue);
  const sketch = useCadUi((s) => s.sketch);
  const histRef = useRef<HTMLDivElement>(null);
  useEffect(() => { const el = histRef.current; if (el && typeof el.scrollTo === "function") el.scrollTo({ top: el.scrollHeight }); }, [history.length]);
  const suggestions = sketch ? [] : suggestCommands(value);
  const onKey = (e: KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === "Enter") { e.preventDefault(); runCommandLine(value, nav); }
    else if (e.key === "Escape") { setValue(""); }
    else if (e.key === "Tab" && suggestions[0]) { e.preventDefault(); setValue(suggestions[0].id + " "); }
  };
  return (
    <div className="shrink-0 bg-cad-tip border-t border-[#0F1114] px-2 py-1 font-mono text-[11px] text-cad-cmdink" aria-label={t("Komut satırı")}>
      <div ref={histRef} className="h-11 overflow-auto grid gap-px content-end" role="log" aria-live="polite">
        {history.length === 0 ? <div className="text-cad-cmdecho">{t("Komut satırı hazır — YARDIM yazın.")}</div> : history.map((h, i) => <div key={i} className={`whitespace-pre-wrap ${TONE[h.tone]}`}>{h.text}</div>)}
      </div>
      <div className="flex items-center gap-1.5 mt-[3px]">
        <span className="text-cad-cmdecho">{sketch ? `CIZ [${sketch.points.length}]:` : "Komut:"}</span>
        <input value={value} disabled={disabled} onChange={(e) => setValue(e.target.value)} onKeyDown={onKey} aria-label={t("Komut")} spellCheck={false} autoComplete="off"
          placeholder={disabled ? t("salt okunur paylaşım — komut girilemez") : sketch ? t(SKETCH_PROMPT) : t("komut veya değer girin — YARDIM")}
          className="flex-1 bg-cad-model border border-cad-tipline text-cad-cmdinput font-mono text-[11px] px-1.5 py-[3px] outline-none focus:border-[#7C8794] placeholder:text-cad-cmdecho disabled:opacity-60" />
      </div>
      {suggestions.length && value.trim() ? (
        <div className="mt-[3px] flex flex-wrap gap-1">
          {suggestions.map((c) => <button key={c.id} type="button" onClick={() => setValue(c.id + (c.args ? " " : ""))} className="px-1.5 py-px text-[11px] border border-cad-tipline bg-cad-cmdchip text-cad-tipink font-mono cursor-pointer hover:bg-cad-tipactive" title={c.help}>{c.id}{c.args ? ` ${c.args}` : ""}</button>)}
        </div>
      ) : null}
    </div>
  );
}
